import { useState, type FormEvent } from 'react';
import { authApi, ApiError } from '../api/client';
import { useAuthStore } from '../stores/auth.store';

export function LoginPage() {
  const setChallenge = useAuthStore((s) => s.setChallenge);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const state = useAuthStore((s) => s.state);
  const error = state.status === 'unauthenticated' ? state.error : null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await authApi.login(email.trim(), password);
      setChallenge(result.challenge_token, result.expires_in_seconds);
    } catch (err) {
      const message = err instanceof ApiError ? err.body.message ?? err.body.error ?? 'Login failed' : 'Login failed';
      setUnauthenticated(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-8">
      <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6 bg-surface-2 rounded-2xl p-8 shadow-2xl">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Aurem</h1>
          <p className="mt-1 text-xs uppercase tracking-widest text-ink-muted">Platform Admin</p>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-xs uppercase tracking-wider text-ink-muted mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-xs uppercase tracking-wider text-ink-muted mb-1.5">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
            />
          </div>
        </div>

        {error && (
          <p role="alert" className="text-sm text-bad">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-primary px-4 py-2.5 font-medium text-ink transition disabled:opacity-50"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="text-center text-xs text-ink-muted">
          A 6-digit verification code will be emailed after password verification.
        </p>
      </form>
    </main>
  );
}
